import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type {
  Webhook,
  WebhookDetail,
  WebhookDelivery,
  WebhookEvent,
  PaginatedResponse,
} from "@/lib/types"

interface WebhookFilters {
  is_active?: boolean
  search?: string
}

export function useWebhooks(filters?: WebhookFilters) {
  const params = new URLSearchParams()
  if (filters?.is_active !== undefined) params.set("is_active", String(filters.is_active))
  if (filters?.search) params.set("search", filters.search)
  const qs = params.toString()

  return useQuery({
    queryKey: ["webhooks", filters],
    queryFn: () =>
      api.get<PaginatedResponse<Webhook>>(`/api/v1/webhooks${qs ? `?${qs}` : ""}`),
  })
}

export function useWebhook(id: string) {
  return useQuery({
    queryKey: ["webhooks", id],
    queryFn: () => api.get<{ data: WebhookDetail }>(`/api/v1/webhooks/${id}`),
    enabled: !!id,
  })
}

export function useCreateWebhook() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: WebhookDetail }>("/api/v1/webhooks", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhooks"] })
    },
  })
}

export function useUpdateWebhook() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: WebhookDetail }>(`/api/v1/webhooks/${id}`, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["webhooks"] })
      qc.invalidateQueries({ queryKey: ["webhooks", id] })
    },
  })
}

export function useDeleteWebhook() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/webhooks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhooks"] })
    },
  })
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: { success: boolean; status?: number; body?: string; error?: string } }>(
        `/api/v1/webhooks/${id}/test`
      ),
  })
}

export function useWebhookDeliveries(webhookId: string, status?: string) {
  const params = new URLSearchParams()
  if (status) params.set("status", status)
  const qs = params.toString()

  return useQuery({
    queryKey: ["webhooks", webhookId, "deliveries", status],
    queryFn: () =>
      api.get<PaginatedResponse<WebhookDelivery>>(
        `/api/v1/webhooks/${webhookId}/deliveries${qs ? `?${qs}` : ""}`
      ),
    enabled: !!webhookId,
  })
}

export function useRetryDelivery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ webhookId, deliveryId }: { webhookId: string; deliveryId: string }) =>
      api.post<{ data: WebhookDelivery }>(
        `/api/v1/webhooks/${webhookId}/deliveries/${deliveryId}/retry`
      ),
    onSuccess: (_, { webhookId }) => {
      qc.invalidateQueries({ queryKey: ["webhooks", webhookId, "deliveries"] })
      qc.invalidateQueries({ queryKey: ["webhooks", webhookId] })
    },
  })
}

export function useAvailableEvents() {
  return useQuery({
    queryKey: ["webhooks", "available_events"],
    queryFn: () =>
      api.get<{ data: WebhookEvent[] }>("/api/v1/webhooks/available_events"),
  })
}
